<template>
  <v-layout
    id="lecture-form"
    justify-center
    wrap
  >
    <v-flex
      xs12
      pl-0
      pr-0
      class="fill-height"
    >
      <v-card class="pt-7 pl-0 pr-0">
        <v-form>
          <v-layout
            wrap
          >
            <v-flex
              xs12
            >
              <v-text-field
                v-model.trim="form.name"
                :error-messages="NameErrors"
                :label="$t('modules.lectureName')+ '*'"
                required
                @input="$v.form.name.$touch()"
                @blur="$v.form.name.$touch()"
              />
            </v-flex>
            <v-flex xs12>
              <v-checkbox
                v-model="form.with_video"
                color="primary"
              >
                <template
                  v-slot:label
                >
                  <span>
                    <b>With Video</b><br>
                  </span>
                </template>
              </v-checkbox>
            </v-flex>
            <template v-if="form.with_video">
              <v-flex
                xs12
                md4
              >
                <v-select
                  v-model="form.video_type"
                  :items="video_types"
                  :error-messages="VideoTypeErrors"
                  :label="$t('modules.lectureVideoProvider')"
                  @input="$v.form.video_type.$touch()"
                  @blur="$v.form.video_type.$touch()"
                />
              </v-flex>
              <v-flex
                xs12
                md8
              >
                <v-text-field
                  v-model.trim="form.video_link"
                  :error-messages="VideoLinkErrors"
                  :label="$t('modules.lectureVideoLink')"
                  placeholder="https://(youtube|vimeo).com/..."
                  @input="$v.form.video_link.$touch()"
                  @blur="$v.form.video_link.$touch()"
                />
              </v-flex>
              <v-flex
                v-if="form.video_link && !$v.form.video_link.$invalid && showVimeoPlayer"
                xs12
              >
                <v-skeleton-loader
                  :boilerplate="downloadVideo"
                  :tile="downloadVideo"
                  type="image"
                >
                  <vueVimeoPlayer
                    v-if="form.video_type === 'vimeo'"
                    :video-id="videoId"
                    :options="{'responsive':true}"
                    @error="catchError"
                    @ready="getIdFromVimeoURL"
                  />
                  <youtube
                    v-if="form.video_type === 'youtube'"
                    ref="youtube"
                    :video-id="videoId"
                    width="100%"
                    @ready="getIdYoutube"
                    @error="catchError"
                  />
                </v-skeleton-loader>
              </v-flex>
            </template>
            <v-flex
              xs12
            >
              <b>{{ $t("modules.lectureShortDescription") }}</b>
            </v-flex>
            <v-flex
              xs12
            >
              <v-textarea
                v-model.trim="form.excerpt"
                outlined
                :error-messages="ExcerptErrors"
                no-resize
                @input="$v.form.excerpt.$touch()"
                @blur="$v.form.excerpt.$touch()"
              />
            </v-flex>
            <v-flex
              xs12
            >
              <b>{{ $t("modules.lectureLongDescription") }}</b>
            </v-flex>
            <v-flex>
              <vue-editor
                id="editor"
                v-model="form.description"
                :editor-toolbar="customToolbar"
                :editor-options="editorSettings"
              />
            </v-flex>
          </v-layout>
          <v-layout>
            <v-flex
              class="text-right"
              pt-0
              pb-0
            >
              <v-btn
                text
                :disabled="loading"
                @click="$emit('closeEditLecture', false)"
              >
                <span>{{ $t('globals.cancelButton') }}</span>
              </v-btn>
              <v-btn
                color="primary"
                :disabled="loading || $v.$invalid || saveVideoLink"
                :loading="loading"
                @click="save()"
              >
                {{ $t('globals.saveButton') }}
              </v-btn>
            </v-flex>
          </v-layout> 
        </v-form>
      </v-card>
    </v-flex>
  </v-layout>
</template>
<script>

const videoUrl = url => {
  if (url == null || url == "") {
    return true;
  }
  const re = /^(https\:\/\/)?((www\.)?(vimeo\.com\/)([0-9]+)$)|((www\.youtube\.com|youtu\.be)\/.+$)/
  return re.test(url);
}

const contains = (param) => (value) => {
  if(param === "youtube") {
    return value.includes(param) || value.includes("youtu.be");
  }
  return value.includes(param)
}

import { validationMixin } from "vuelidate";
import {
  required,
  url,
  minLength,
  maxLength
} from "vuelidate/lib/validators";
import { mapGetters } from 'vuex';
import { VueEditor } from "vue2-editor";
import { vueVimeoPlayer } from 'vue-vimeo-player';
import { Youtube, getIdFromUrl } from 'vue-youtube';
import LECTURE_CREATE_MUTATION from "@/graphql/mutations/modules/LmsLectureCreate.gql";
import LECTURE_UPDATE_MUTATION from "@/graphql/mutations/modules/LmsLectureUpdate.gql";

export default {
  name: 'NewOrEditLecture',
  components: {
    VueEditor,
    vueVimeoPlayer,
    Youtube
  },
  mixins: [validationMixin],
  props: {
    whitelabelUuid: {
      type: String,
      default: null
    },
    lecture: {
      type: Object,
      default: null
    },
    courseUuid: {
      type: String,
      default: null
    },
    sectionId: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      loading: false,
      form: {
        name: "",
        with_video: true,
        video_type: "",
        video_link: "",
        excerpt: "",
        description: ""
      },
      video_types: [],
      lecture_types: "",
      videoId: '',
      downloadVideo: false,
      showVimeoPlayer: true,
      saveVideoLink: false,
      customToolbar: [
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        ['blockquote'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'script': 'sub'}, { 'script': 'super' }],
        [{ 'indent': '-1'}, { 'indent': '+1' }],
        [{ 'direction': 'rtl' }],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'align': []}, 'link', 'image'],

        ['clean']
      ],
      editorSettings: {
        modules: {
          imageDrop: true,
          imageResize: {
            displaySize: true,
          }
        }
      },
    }
  },
  computed: {
    ...mapGetters("whitelabel", ["getWhiteLabel"]),
    moduleData() {
      if (this.getWhiteLabel && this.getWhiteLabel.modules.length > 0) {
        return this.getWhiteLabel.modules.find(item => item.module == 'lms')
      }
      return null
    },
    rules() {
      return {
        name: {
          required,
          minLength: minLength(3),
          maxLength: maxLength(100),
        },
        video_type: {
        },
        video_link: {
          url,
          videoUrl: contains(this.form.video_type),
          maxLength: maxLength(100),
        },
        excerpt: {
          maxLength: maxLength(5000),
        }
      }
    },
    NameErrors() {
      const errors = [];
      if (!this.$v.form.name.$dirty) return errors;
      !this.$v.form.name.required && errors.push(this.$t("profile.reqField"));
      !this.$v.form.name.minLength &&
        errors.push(this.$t("whitelabel.minError", {char: this.$v.form.name.$params.minLength.min}));
      !this.$v.form.name.maxLength &&
        errors.push(this.$t("whitelabel.maxError", {char: this.$v.form.name.$params.maxLength.max}));
      return errors;
    },
    VideoTypeErrors() {
      const errors = [];
      if (!this.$v.form.video_type.$dirty) return errors;
      // !this.$v.form.video_type.required && errors.push(this.$t("profile.reqField"));
      return errors;
    },
    VideoLinkErrors() {
      const errors = [];
      if (!this.$v.form.video_link.$dirty) return errors;
      !this.$v.form.video_link.maxLength &&
        errors.push(this.$t("whitelabel.maxError", {char: this.$v.form.video_link.$params.maxLength.max}));
      !this.$v.form.video_link.url &&
        errors.push(this.$t("modules.lectureUrlError"));
      !this.$v.form.video_link.videoUrl &&
        errors.push(this.$t("modules.lectureUrlError"));
      return errors;
    },
    ExcerptErrors() {
      const errors = [];
      if (!this.$v.form.excerpt.$dirty) return errors;
      !this.$v.form.excerpt.maxLength &&
        errors.push(this.$t("whitelabel.maxError", {char: this.$v.form.excerpt.$params.maxLength.max}));
      return errors;
    },
  },
  watch: {
    "videoId": {
      handler: function(newVal) {
        if(!newVal) {
          this.showVimeoPlayer = false;
          this.saveVideoLink = true;
        } else {
          this.showVimeoPlayer = true;
          this.saveVideoLink = false;
        }
      }
    },
    "form.video_link": {
      handler: function(newVal) {
        if(!newVal) {
          if(this.form.video_type === "youtube") {
            this.getIdYoutube()
          }
          if(this.form.video_type === "vimeo") {
            this.getIdFromVimeoURL()
          }
          this.showVimeoPlayer = false;
          this.saveVideoLink = false;
        } else {
          if(this.form.video_type === "youtube") {
            this.getIdYoutube();
          }
          if(this.form.video_type === "vimeo") {
            this.getIdFromVimeoURL()
          }
          this.saveVideoLink = false;
          this.showVimeoPlayer = true;
        }
     }
    },
    "form.with_video": {
      handler(val) {
        if(!val) {
          this.form.video_type = '';
          this.form.video_link = '';
          this.saveVideoLink = false;
        }
     }
    },
  },
  mounted() {
    const formLecture = JSON.parse(JSON.stringify(this.lecture))
    if (formLecture) {
      if (formLecture.video_type) {
        formLecture.with_video = true;
      }
      this.form = formLecture
    }
    if (this.moduleData) {
      this.video_types = this.moduleData.public_config.video_providers;
      this.lecture_types = this.moduleData.public_config.lecture_types[0];
    }
  },
  unmounted() {
    this.$refs.youtube.player.destroy();
  },
  validations() {
    return {
      form: this.rules
    };
  },
  methods: {
    catchError(){
      this.videoId = '';
      this.showVimeoPlayer = false;
      this.downloadVideo = false;
    },
    getIdFromVimeoURL() {
      const checkUrl = /(vimeo(pro)?\.com)\/(?:[^\d]+)?(\d+)\??(.*)?$/
      this.videoId = this.form.video_type === 'vimeo' &&
              this.form.video_link &&
              !this.$v.form.video_link.$invalid  ? checkUrl.exec(this.form.video_link)[3] : "";
      this.downloadVideo = true;
    },
    getIdYoutube () {
      this.videoId = this.form.video_type === 'youtube' &&
              this.form.video_link &&
              !this.$v.form.video_link.$invalid ? getIdFromUrl(this.form.video_link) : "";
    },
    save() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return
      }

      this.loading = true;
      this.form.whitelabel_uuid = this.whitelabelUuid;
      this.form.course_uuid = this.courseUuid;
      this.form.section_id = this.sectionId;
      this.form.type = this.lecture_types;
      if(this.lecture) {
        this.form.lecture_id = this.lecture.id
      }
      this.$apollo
        .mutate({
          mutation: !this.lecture ? LECTURE_CREATE_MUTATION : LECTURE_UPDATE_MUTATION,
          variables: this.form
        })
        .then(data => {
          let message =  data.data.moduleLmsLectureCreate ? data.data.moduleLmsLectureCreate.message : data.data.moduleLmsLectureUpdate.message
          this.$store.commit('app/setNotifier', message);
          this.loading = false;
          this.$emit('closeEditLecture', false);
        })
        .catch(error => {
          this.errorHandler(error);
          this.$emit('closeEditLecture', false);
        });
    }
  }
}
</script>
<style lang="scss" scoped>
#editor {
  height: 400px;
}
#lecture-form {
  small {
    font-size: 12px;
    font-weight: 600 !important;
    color: #f55a4e !important;
    caret-color: #f55a4e !important;
  }  
  .embed-container {
    width: 100% !important;
  }
}
</style>